import { useEffect, useState } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  getDefaultOperators,
} from '@/components'
import {
  type FieldSchema,
  type Filter,
  type FilterOperator,
  type Operator,
  useDialogActions,
  useDialogData,
  useDialogIsOpen,
  useFilters,
} from '@/stores'
import { AmountFieldset } from './AmountFieldset'
import { ConditionSelector } from './ConditionSelector'
import { DateFieldset } from './DateFieldset'
import type { SelectOption } from './inputs/types'
import { SelectFieldset } from './SelectFieldset'
import { TextFieldset } from './TextFieldset'

export interface FilterDialogProps {
  id: string
  onApply: (filter: Filter) => void
  getUniqueValues?: (field: string) => SelectOption[]
}

const stringify = (value?: string | number) => (value ? `${value}` : '')

export function FilterDialog({
  id,
  onApply,
  getUniqueValues,
}: FilterDialogProps) {
  const isOpen = useDialogIsOpen(id)
  const filters = useFilters()
  const { closeDialog } = useDialogActions()
  const data = useDialogData({ schema: null, filter: null }) as {
    schema: FieldSchema | null
  }
  const schema = data?.schema
  const filter = filters.find((filter) => filter.field === schema?.field)

  const [operator, setOperator] = useState('')
  const [value, setValue] = useState('')
  const [value2, setValue2] = useState('')
  const [selectedValues, setSelectedValues] = useState<string[]>([])

  useEffect(() => {
    if (!isOpen || !schema) return

    const operators = schema.operators || getDefaultOperators(schema.type)
    const defaultOp =
      Array.isArray(operators) && operators.length > 0
        ? typeof operators[0] === 'string'
          ? operators[0]
          : operators[0].value
        : 'equals'

    if (!filter) {
      setOperator(defaultOp)
      setValue('')
      setValue2('')
      setSelectedValues([])
      return
    }

    setOperator(filter.operator || defaultOp)
    if (schema.type === 'select') {
      const values = Array.isArray(filter.value) ? filter.value : [filter.value]
      setSelectedValues(values.map(stringify))
    } else if (schema.type === 'tags') {
      const tags = Array.isArray(filter.value)
        ? filter.value.map((tag) => tag.name)
        : [filter.value.name]
      setSelectedValues(tags.map(stringify))
    } else {
      setValue(stringify(filter.value))
      setValue2(stringify(filter.value2))
    }
  }, [isOpen, schema, filter])

  if (!schema) return null

  const operators = schema.operators || getDefaultOperators(schema.type)
  const operatorOptions: Operator[] = operators.map((op) =>
    typeof op === 'string' ? { value: op, label: op } : op,
  )

  const handleApply = () => {
    const isSelectLike = ['select', 'tags'].includes(schema.type)
    const filterValue = isSelectLike
      ? schema.multiple !== false
        ? selectedValues
        : selectedValues[0]
      : schema.type === 'number'
        ? parseFloat(value)
        : value

    const newFilter: Filter = {
      id: filter?.id || crypto.randomUUID(),
      field: schema.field,
      operator: operator as FilterOperator,
      value: filterValue,
    }

    if (!isSelectLike && operator === 'between') {
      newFilter.value2 = schema.type === 'number' ? parseFloat(value2) : value2
    }

    onApply(newFilter)
    closeDialog(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isApplyDisabled) {
      handleApply()
    }
  }

  const isApplyDisabled = ['select', 'tags'].includes(schema.type)
    ? selectedValues.length === 0
    : !value || (operator === 'between' && !value2)

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {filter ? 'Edit' : 'Add'} {schema.label} Filter
          </DialogTitle>
          <DialogDescription>
            Configure the filter criteria below.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {['select', 'tags'].includes(schema.type) ? (
            <SelectFieldset
              schema={schema}
              getUniqueValues={getUniqueValues}
              selectedValues={selectedValues}
              setSelectedValues={setSelectedValues}
            />
          ) : (
            <>
              <ConditionSelector
                operatorOptions={operatorOptions}
                operator={operator}
                setOperator={setOperator}
              />

              {schema.type === 'date' && (
                <DateFieldset
                  operator={operator}
                  value={value}
                  setValue={setValue}
                  value2={value2}
                  setValue2={setValue2}
                />
              )}

              {schema.type === 'number' && (
                <AmountFieldset
                  operator={operator}
                  value={value}
                  setValue={setValue}
                  value2={value2}
                  setValue2={setValue2}
                  handleKeyDown={handleKeyDown}
                />
              )}

              {!['date', 'number'].includes(schema.type) && (
                <TextFieldset
                  operator={operator}
                  value={value}
                  setValue={setValue}
                  value2={value2}
                  setValue2={setValue2}
                  handleKeyDown={handleKeyDown}
                />
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button theme="outline" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={isApplyDisabled}>
            {filter ? 'Update Filter' : 'Apply Filter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
